/**
 * Core Layer - Storage Client
 * Framework-agnostic wrapper for browser storage (cookies, localStorage)
 */

import { COOKIE_SETTINGS, DATE_STRINGS } from "@/constants";



/**
 * Cookie options
 */
export interface CookieOptions {
  path?: string;
  maxAge?: number;
  expires?: Date;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
  httpOnly?: boolean;
  domain?: string;
}

/**
 * Storage Client - Unified interface for browser storage
 */
export const storageClient = {
  /**
   * Get a cookie value by name
   */
  getCookie: (name: string): string | undefined => {
    if (typeof document === "undefined") return undefined;

    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((c) => c.startsWith(`${name}=`));

    // Use substring to skip the "name=" prefix — avoids truncating values
    // that contain "=" characters (e.g. Base64-padded JWTs, JSON strings)
    if (!cookie) return undefined;
    return cookie.substring(name.length + 1);
  },

  /**
   * Set a cookie with options
   */
  setCookie: (
    name: string,
    value: string,
    options: CookieOptions = {}
  ): void => {
    if (typeof document === "undefined") return;

    const {
      path = COOKIE_SETTINGS.PATH,
      maxAge,
      expires,
      secure = COOKIE_SETTINGS.SECURE,
      sameSite = COOKIE_SETTINGS.SAME_SITE,
      httpOnly = COOKIE_SETTINGS.HTTP_ONLY,
      domain,
    } = options;

    let cookieString = `${name}=${value}; path=${path}`;

    if (maxAge !== undefined) cookieString += `; max-age=${maxAge}`;
    if (expires) cookieString += `; expires=${expires.toUTCString()}`;
    if (secure) cookieString += "; secure";
    if (sameSite) cookieString += `; samesite=${sameSite}`;
    if (httpOnly) cookieString += "; httponly";
    if (domain) cookieString += `; domain=${domain}`;

    document.cookie = cookieString;
  },

  /**
   * Delete a cookie by name
   */
  deleteCookie: (name: string, path = COOKIE_SETTINGS.PATH): void => {
    if (typeof document === "undefined") return;

    document.cookie = `${name}=; path=${path}; expires=${DATE_STRINGS.EPOCH}`;
  },

  /**
   * Check if a cookie exists
   */
  hasCookie: (name: string): boolean => {
    if (typeof document === "undefined") return false;

    const cookies = document.cookie.split("; ");
    return cookies.some((cookie) => cookie.startsWith(`${name}=`));
  },

  /**
   * Get a value from localStorage
   */
  getLocalStorage: <T>(key: string, fallback: T): T => {
    if (typeof window === "undefined") return fallback;

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : fallback;
    } catch {
      return fallback;
    }
  },

  /**
   * Set a value in localStorage
   */
  setLocalStorage: <T>(key: string, value: T): void => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Error setting localStorage:", error);
    }
  },

  /**
   * Remove a value from localStorage
   */
  removeLocalStorage: (key: string): void => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing from localStorage:", error);
    }
  },

  /**
   * Get a value from sessionStorage
   */
  getSessionStorage: <T>(key: string, fallback: T): T => {
    if (typeof window === "undefined") return fallback;

    try {
      const item = window.sessionStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : fallback;
    } catch {
      return fallback;
    }
  },

  /**
   * Set a value in sessionStorage
   */
  setSessionStorage: <T>(key: string, value: T): void => {
    if (typeof window === "undefined") return;

    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Error setting sessionStorage:", error);
    }
  },

  /**
   * Remove a value from sessionStorage
   */
  removeSessionStorage: (key: string): void => {
    if (typeof window === "undefined") return;

    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing from sessionStorage:", error);
    }
  },
};

// Convenience exports for backward compatibility
export const getCookie = storageClient.getCookie;
export const setCookie = storageClient.setCookie;
export const deleteCookie = storageClient.deleteCookie;
export const hasCookie = storageClient.hasCookie;

export default storageClient;
